/**
 * Task Service
 * Handles all task and workflow management API operations
 * 
 * @module services/taskService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * Task Service Class
 * Provides methods for task CRUD, assignments, status updates, and workflow tracking
 * 
 * @class TaskService
 * @extends BaseService
 * 
 * @example
 * import taskService from './services/taskService';
 * 
 * // Get all tasks
 * const tasks = await taskService.getAll({ status: 'in_progress' });
 * 
 * // Create task
 * await taskService.create({
 *   title: 'Review Q4 Budget',
 *   assigneeId: 'user-123',
 *   priority: 'high'
 * });
 */
class TaskService extends BaseService {
  constructor() {
    super('/tasks');
  }

  /**
   * Get all tasks with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.status] - Filter by status (todo, in_progress, review, completed, cancelled)
   * @param {string} [params.priority] - Filter by priority (low, medium, high, urgent)
   * @param {string} [params.assigneeId] - Filter by assignee
   * @param {string} [params.creatorId] - Filter by creator
   * @param {string} [params.departmentId] - Filter by department
   * @param {string} [params.projectId] - Filter by project
   * @param {string} [params.dueDate] - Filter by due date
   * @param {boolean} [params.overdue] - Only overdue tasks
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getAll(params = {}) {
    return this.get('', { params });
  }

  /**
   * Get task by ID
   * 
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Task details
   */
  async getById(taskId) {
    return this.get(`/${taskId}`);
  }

  /**
   * Create new task
   * 
   * @param {Object} taskData - Task data
   * @param {string} taskData.title - Task title
   * @param {string} [taskData.description] - Description
   * @param {string} [taskData.status='todo'] - Status
   * @param {string} [taskData.priority='medium'] - Priority
   * @param {string} [taskData.assigneeId] - Assignee user ID
   * @param {string} [taskData.departmentId] - Department ID
   * @param {string} [taskData.projectId] - Project ID
   * @param {string} [taskData.dueDate] - Due date
   * @param {Array<string>} [taskData.tags] - Tags
   * @param {Object} [taskData.metadata] - Additional metadata
   * @returns {Promise<Object>} Created task
   */
  async create(taskData) {
    return this.post('', taskData);
  }

  /**
   * Update task
   * 
   * @param {string} taskId - Task ID
   * @param {Object} taskData - Updated task data
   * @returns {Promise<Object>} Updated task
   */
  async update(taskId, taskData) {
    return this.put(`/${taskId}`, taskData);
  }

  /**
   * Partial update task
   * 
   * @param {string} taskId - Task ID
   * @param {Object} updates - Partial updates
   * @returns {Promise<Object>} Updated task
   */
  async partialUpdate(taskId, updates) {
    return this.patch(`/${taskId}`, updates);
  }

  /**
   * Delete task
   * 
   * @param {string} taskId - Task ID
   * @returns {Promise<void>}
   */
  async delete(taskId) {
    return this.delete(`/${taskId}`);
  }

  /**
   * Search tasks with cancellation
   * 
   * @param {string} query - Search query
   * @param {Object} [options={}] - Search options
   * @param {string} [options.status] - Filter by status
   * @param {number} [options.limit=10] - Max results
   * @returns {Promise<Array>} Matching tasks
   */
  async search(query, options = {}) {
    return this.getWithCancel('/search', 'task-search', {
      params: { q: query, ...options }
    });
  }

  /**
   * Update task status
   * 
   * @param {string} taskId - Task ID
   * @param {string} status - New status
   * @param {string} [comment] - Optional comment
   * @returns {Promise<Object>} Updated task
   */
  async updateStatus(taskId, status, comment = null) {
    return this.patch(`/${taskId}/status`, { status, comment });
  }

  /**
   * Assign task to user
   * 
   * @param {string} taskId - Task ID
   * @param {string} assigneeId - User ID
   * @param {string} [note] - Assignment note
   * @returns {Promise<Object>} Updated task
   */
  async assign(taskId, assigneeId, note = null) {
    return this.patch(`/${taskId}/assign`, { assigneeId, note });
  }

  /**
   * Reassign task to different user
   * 
   * @param {string} taskId - Task ID
   * @param {string} newAssigneeId - New assignee user ID
   * @param {string} [reason] - Reassignment reason
   * @returns {Promise<Object>} Updated task
   */
  async reassign(taskId, newAssigneeId, reason = null) {
    return this.patch(`/${taskId}/reassign`, { assigneeId: newAssigneeId, reason });
  }

  /**
   * Update task priority
   * 
   * @param {string} taskId - Task ID
   * @param {string} priority - New priority (low, medium, high, urgent)
   * @returns {Promise<Object>} Updated task
   */
  async updatePriority(taskId, priority) {
    return this.patch(`/${taskId}/priority`, { priority });
  }

  /**
   * Update task due date
   * 
   * @param {string} taskId - Task ID
   * @param {string} dueDate - New due date
   * @param {string} [reason] - Reason for change
   * @returns {Promise<Object>} Updated task
   */
  async updateDueDate(taskId, dueDate, reason = null) {
    return this.patch(`/${taskId}/due-date`, { dueDate, reason });
  }

  /**
   * Add comment to task
   * 
   * @param {string} taskId - Task ID
   * @param {Object} commentData - Comment data
   * @param {string} commentData.text - Comment text
   * @param {Array<string>} [commentData.attachments] - Attachment URLs
   * @returns {Promise<Object>} Created comment
   */
  async addComment(taskId, commentData) {
    return this.post(`/${taskId}/comments`, commentData);
  }

  /**
   * Get task comments
   * 
   * @param {string} taskId - Task ID
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Comments
   */
  async getComments(taskId, params = {}) {
    return this.get(`/${taskId}/comments`, { params });
  }

  /**
   * Add attachment to task
   * 
   * @param {string} taskId - Task ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Attachment info
   */
  async addAttachment(taskId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.post(`/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Get task attachments
   * 
   * @param {string} taskId - Task ID
   * @returns {Promise<Array>} Attachments
   */
  async getAttachments(taskId) {
    return this.get(`/${taskId}/attachments`);
  }

  /**
   * Delete attachment
   * 
   * @param {string} taskId - Task ID
   * @param {string} attachmentId - Attachment ID
   * @returns {Promise<void>}
   */
  async deleteAttachment(taskId, attachmentId) {
    return this.delete(`/${taskId}/attachments/${attachmentId}`);
  }

  /**
   * Get task history/activity log
   * 
   * @param {string} taskId - Task ID
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Activity history
   */
  async getHistory(taskId, params = {}) {
    return this.get(`/${taskId}/history`, { params });
  }

  /**
   * Get tasks by assignee
   * 
   * @param {string} userId - User ID
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Assigned tasks
   */
  async getByAssignee(userId, params = {}) {
    return this.get('/by-assignee', { params: { userId, ...params } });
  }

  /**
   * Get tasks by department
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Department tasks
   */
  async getByDepartment(departmentId, params = {}) {
    return this.get('/by-department', { params: { departmentId, ...params } });
  }

  /**
   * Get tasks by project
   * 
   * @param {string} projectId - Project ID
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Project tasks
   */
  async getByProject(projectId, params = {}) {
    return this.get('/by-project', { params: { projectId, ...params } });
  }

  /**
   * Get my tasks (for current user)
   * 
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Current user's tasks
   */
  async getMyTasks(params = {}) {
    return this.get('/my-tasks', { params });
  }

  /**
   * Get overdue tasks
   * 
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Overdue tasks
   */
  async getOverdue(params = {}) {
    return this.get('/overdue', { params });
  }

  /**
   * Get upcoming tasks (due soon)
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.days=7] - Number of days to look ahead
   * @returns {Promise<Array>} Upcoming tasks
   */
  async getUpcoming(params = {}) {
    return this.get('/upcoming', { params });
  }

  /**
   * Get task statistics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.groupBy] - Group by field (status, priority, assignee, department)
   * @returns {Promise<Object>} Task statistics
   */
  async getStatistics(params = {}) {
    return this.get('/statistics', { params });
  }

  /**
   * Get task analytics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @param {string} [params.metric] - Specific metric
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics(params = {}) {
    return this.get('/analytics', { params });
  }

  /**
   * Get workflow progress
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.projectId] - Filter by project
   * @param {string} [params.departmentId] - Filter by department
   * @returns {Promise<Object>} Workflow progress data
   */
  async getWorkflowProgress(params = {}) {
    return this.get('/workflow-progress', { params });
  }

  /**
   * Bulk operations
   * 
   * @param {Object} bulkData - Bulk operation data
   * @param {Array<string>} bulkData.taskIds - Array of task IDs
   * @param {string} bulkData.action - Action (updateStatus, updatePriority, assign, delete)
   * @param {Object} [bulkData.data] - Additional data for the action
   * @returns {Promise<{success: number, failed: number, results: Array}>}
   */
  async bulkOperation(bulkData) {
    return this.post('/bulk', bulkData);
  }

  /**
   * Clone/duplicate task
   * 
   * @param {string} taskId - Task ID to clone
   * @param {Object} [overrides={}] - Fields to override in cloned task
   * @returns {Promise<Object>} Cloned task
   */
  async clone(taskId, overrides = {}) {
    return this.post(`/${taskId}/clone`, overrides);
  }

  /**
   * Export tasks
   * 
   * @param {Object} [params={}] - Export parameters
   * @param {string} [params.format='csv'] - Export format (csv, xlsx, pdf)
   * @param {Array<string>} [params.fields] - Fields to include
   * @param {Object} [params.filters] - Filters to apply
   * @returns {Promise<Blob>} Export file
   */
  async export(params = {}) {
    return this.get('/export', {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Import tasks from file
   * 
   * @param {File} file - CSV or Excel file
   * @param {Object} [options={}] - Import options
   * @param {boolean} [options.skipErrors=true] - Skip rows with errors
   * @returns {Promise<{imported: number, failed: number, errors: Array}>}
   */
  async import(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    
    return this.post('/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
}

// Export singleton instance
const taskService = new TaskService();
export default taskService;
